import React from 'react';
import { useTheme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { useAnimate, useAnimateBar, useDrawingArea } from '@mui/x-charts/hooks';
import { PiecewiseColorLegend } from '@mui/x-charts/ChartsLegend';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { interpolateObject } from '@mui/x-charts-vendor/d3-interpolate';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';

// Componente de barra con fondo sombreado
function BarShadedBackground(props) {
  const { ownerState, skipAnimation, id, dataIndex, xOrigin, yOrigin, ...other } = props;
  const theme = useTheme();
  
  const animatedProps = useAnimateBar(props);
  const { width } = useDrawingArea();
  
  return (
    <React.Fragment>
      <rect
        {...other}
        fill={(theme.vars || theme).palette.text.primary}
        opacity={theme.palette.mode === 'dark' ? 0.05 : 0.1}
        x={other.x}
        width={width}
      />
      <rect
        {...other}
        filter={ownerState.isHighlighted ? 'brightness(120%)' : undefined}
        opacity={ownerState.isFaded ? 0.3 : 1}
        data-highlighted={ownerState.isHighlighted || undefined}
        data-faded={ownerState.isFaded || undefined}
        {...animatedProps}
      />
    </React.Fragment>
  );
}

// Texto estilizado para las etiquetas
const Text = styled('text')(({ theme }) => ({
  ...theme?.typography?.body2,
  stroke: 'none',
  fill: (theme.vars || theme).palette.common.white,
  transition: 'opacity 0.2s ease-in, fill 0.2s ease-in',
  textAnchor: 'start',
  dominantBaseline: 'central',
  pointerEvents: 'none',
  fontWeight: 600,
  fontSize: '12px',
}));

// Componente de etiqueta en la base
function BarLabelAtBase(props) {
  const {
    seriesId,
    dataIndex,
    color,
    isFaded,
    isHighlighted,
    classes,
    xOrigin,
    yOrigin,
    x,
    y,
    width,
    height,
    layout,
    skipAnimation,
    ...otherProps
  } = props;

  const animatedProps = useAnimate(
    { x: xOrigin + 8, y: y + height / 2 },
    {
      initialProps: { x: xOrigin, y: y + height / 2 },
      createInterpolator: interpolateObject,
      transformProps: (p) => p,
      applyProps: (element, p) => {
        element.setAttribute('x', p.x.toString());
        element.setAttribute('y', p.y.toString());
      },
      skip: skipAnimation,
    },
  );

  return <Text {...otherProps} {...animatedProps} />;
}

// Componente principal
const MaterialBarChartHorizontal = ({ data, title = "Estados de Similitud por Símbolo" }) => {
  console.log('MaterialBarChartHorizontal - data received:', data?.length, 'items');
  
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.log('MaterialBarChartHorizontal - No data available');
    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>No hay datos disponibles para el gráfico de barras</Typography>
      </Paper>
    );
  }

  // Convertir datos - mostrar todos los elementos disponibles
  const dataset = data.map((item, index) => {
    let similarity = 0;
    let symbol = '';
    
    if (typeof item === 'object' && item.name && item.value !== undefined) {
      symbol = item.name;
      similarity = item.value;
    } else if (Array.isArray(item) && item.length >= 2) {
      symbol = item[0];
      similarity = item[1];
    } else {
      symbol = `Símbolo ${index + 1}`;
      similarity = typeof item === 'number' ? item : 0;
    }

    // Truncar nombres largos
    const truncatedSymbol = symbol.length > 18 ? symbol.substring(0, 18) + '...' : symbol;
    
    return {
      symbol: truncatedSymbol,
      similarity: Math.round(similarity * 100), // Convertir a porcentaje
      fullName: symbol // Mantener nombre completo para tooltip
    };
  });

  console.log('MaterialBarChartHorizontal - Final dataset:', dataset.length, 'items processed');

  return (
    <Paper sx={{ 
      p: 1.5,  
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: 2,
      boxShadow: 1
    }}>
      {/* Header */}
      <Box sx={{ mb: 1 }}>
        {title && (
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 0.5 }}>
            {title}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {data.length} elementos analizados - Mostrando todos
        </Typography>
      </Box>

      {/* BarChart Container */}
      <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
        <BarChart
          width={600}  // Ancho fijo optimizado para el contenedor
          height={Math.max(320, dataset.length * 35 + 100)}  // Altura dinámica basada en cantidad de elementos
          dataset={dataset}
          series={[
            {
              id: 'similarity',
              dataKey: 'similarity',
              stack: 'similarity_stack',
              valueFormatter: (value) => `${value}%`,
            },
          ]}
          layout="horizontal"
          xAxis={[
            {
              id: 'similarity_axis',
              min: 0,
              max: 100,
              colorMap: {
                type: 'piecewise',
                thresholds: [30, 70],
                colors: ['#ef4444', '#fbbf24', '#22c55e'],
              },
              valueFormatter: (value) => `${value}%`,
            },
          ]}
          barLabel={(v) => `${v.value}%`}
          yAxis={[
            {
              scaleType: 'band',
              dataKey: 'symbol',
              width: 75,  // Reducir más el ancho del eje Y para maximizar espacio de barras
            },
          ]}
          slots={{
            legend: PiecewiseColorLegend,
            barLabel: BarLabelAtBase,
            bar: BarShadedBackground,
            tooltip: ChartsTooltip,
          }}
          slotProps={{
            tooltip: {
              trigger: 'item',
              itemContent: ({ dataIndex, series }) => {
                if (dataIndex !== undefined && dataset[dataIndex]) {
                  const item = dataset[dataIndex];
                  return (
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {item.fullName}
                      </Typography>
                      <Typography variant="caption">
                        Similitud: {item.similarity}%
                      </Typography>
                    </Box>
                  );
                }
                return null;
              },
            },
            legend: {
              axisDirection: 'x',
              markType: 'square',
              labelPosition: 'inline-start',
              labelFormatter: ({ index }) => {
                if (index === 0) {
                  return 'Baja similitud (<30%)';
                }
                if (index === 1) {
                  return 'Similitud media (30-70%)';
                }
                return 'Alta similitud (>70%)';
              },
            },
          }}
          margin={{
            left: 40,    // Reducir aún más el margen izquierdo
            right: 40,   // Aumentar un poco el margen derecho para balance
            top: 10,
            bottom: 150
          }}
        />
      </Box>
    </Paper>
  );
};

export default MaterialBarChartHorizontal;